import { supabase } from '@/lib/supabase';

// Obtener productos con filtros
export const fetchProducts = async (filters = {}) => {
  try {
    let query = supabase
      .from('products')
      .select(`
        id, 
        code, 
        description, 
        price, 
        active, 
        images, 
        main_image_index, 
        sizes, 
        colors, 
        temporada, 
        composicion, 
        venta_solo_stock,
        created_at,
        updated_at
      `, { count: 'exact' });

    // Aplicar filtros
    if (filters.searchTerm) {
      query = query.or(`code.ilike.%${filters.searchTerm}%,description.ilike.%${filters.searchTerm}%`);
    }

    if (filters.temporada && filters.temporada !== 'all') {
      query = query.eq('temporada', filters.temporada);
    }

    if (filters.tagIds && filters.tagIds.length > 0) {
      // Filtrar por etiquetas usando una subconsulta
      const { data: productIds, error: tagError } = await supabase
        .from('product_tags')
        .select('product_id')
        .in('tag_id', filters.tagIds);
      
      if (tagError) throw tagError;
      
      if (productIds && productIds.length > 0) {
        const ids = productIds.map(p => p.product_id);
        query = query.in('id', ids);
      } else {
        // Si no hay productos con esas etiquetas, retornar array vacío
        return { data: [], count: 0, error: null };
      }
    }

    if (filters.active !== undefined) {
      query = query.eq('active', filters.active);
    }

    // Aplicar paginación si se especifica
    if (filters.page && filters.itemsPerPage) {
      const start = (filters.page - 1) * filters.itemsPerPage;
      const end = start + filters.itemsPerPage - 1;
      query = query.range(start, end);
    }

    const { data, error, count } = await query.order('code', { ascending: true });

    if (error) throw error;

    // Si se solicitan etiquetas, obtenerlas para cada producto
    if (filters.includeTags) {
      const productsWithTags = await Promise.all(
        data.map(async (product) => {
          const { data: tagLinks } = await supabase
            .from('product_tags')
            .select(`
              tag:tags(id, name)
            `)
            .eq('product_id', product.id);
          
          return {
            ...product,
            tags: tagLinks?.map(t => t.tag) || []
          };
        })
      );
      
      return { data: productsWithTags, count, error: null };
    }

    return { data, count, error: null };
  } catch (error) {
    console.error('Error fetching products:', error);
    return { data: [], count: 0, error };
  }
};

// Obtener temporadas únicas
export const fetchTemporadas = async () => {
  try {
    const { data, error } = await supabase
      .from('products')
      .select('temporada')
      .not('temporada', 'is', null)
      .not('temporada', 'eq', '')
      .order('temporada');

    if (error) throw error;

    // Obtener valores únicos y ordenados
    const temporadas = [...new Set(data.map(item => item.temporada))].sort();
    return { data: temporadas, error: null };
  } catch (error) {
    console.error('Error fetching temporadas:', error);
    return { data: [], error };
  }
};

// Obtener todas las etiquetas
export const fetchTags = async () => {
  try {
    const { data, error } = await supabase
      .from('tags')
      .select('*')
      .order('name');

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching tags:', error);
    return { data: [], error };
  }
};

// Obtener etiquetas de un producto específico
export const fetchProductTags = async (productId) => {
  try {
    const { data, error } = await supabase
      .from('product_tags')
      .select(`
        tag:tags(id, name)
      `)
      .eq('product_id', productId);

    if (error) throw error;
    
    const tags = data?.map(t => t.tag) || [];
    return { data: tags, error: null };
  } catch (error) {
    console.error('Error fetching product tags:', error);
    return { data: [], error };
  }
};

// Crear o actualizar producto
export const saveProduct = async (productData, productId = null) => {
  try {
    let result;
    
    if (productId) {
      // Actualizar producto existente
      const { data, error } = await supabase
        .from('products')
        .update(productData)
        .eq('id', productId)
        .select()
        .single();
      
      if (error) throw error;
      result = data;
    } else {
      // Crear nuevo producto
      const { data, error } = await supabase
        .from('products')
        .insert(productData)
        .select()
        .single();
      
      if (error) throw error;
      result = data;
    }

    return { data: result, error: null };
  } catch (error) {
    console.error('Error saving product:', error);
    return { data: null, error };
  }
};

// Actualizar etiquetas de un producto
export const updateProductTags = async (productId, tagIds) => {
  try {
    // Eliminar etiquetas existentes
    await supabase
      .from('product_tags')
      .delete()
      .eq('product_id', productId);

    // Insertar nuevas etiquetas
    if (tagIds && tagIds.length > 0) {
      const relations = tagIds.map(tagId => ({
        product_id: productId,
        tag_id: tagId
      }));

      const { error } = await supabase
        .from('product_tags')
        .insert(relations);

      if (error) throw error;
    }

    return { error: null };
  } catch (error) {
    console.error('Error updating product tags:', error);
    return { error };
  }
};

// Eliminar producto
export const deleteProduct = async (productId) => {
  try {
    // Primero eliminar las etiquetas asociadas
    await supabase
      .from('product_tags')
      .delete()
      .eq('product_id', productId);

    // Luego eliminar el producto
    const { error } = await supabase
      .from('products')
      .delete()
      .eq('id', productId);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Error deleting product:', error);
    return { error };
  }
};
